import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import { 
  Plus, 
  Edit2, 
  Trash2,
  AlertCircle,
  Gavel,
  CheckCircle2,
  XCircle,
  Clock,
  UserX,
  CreditCard,
  AlertTriangle,
  Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fine categories
const FINE_CATEGORIES = [
  { value: 'late', label: 'Late', icon: Clock },
  { value: 'absent', label: 'Absent', icon: UserX },
  { value: 'yellow_card', label: 'Yellow Card', icon: AlertTriangle },
  { value: 'red_card', label: 'Red Card', icon: XCircle }
];

const getCategoryLabel = (category) => {
  const cat = FINE_CATEGORIES.find(c => c.value === category);
  return cat ? cat.label : category;
};

const getCategoryIcon = (category) => {
  const cat = FINE_CATEGORIES.find(c => c.value === category);
  return cat ? cat.icon : AlertTriangle;
};

const getCategoryColor = (category) => {
  switch (category) {
    case 'late':
      return 'bg-amber-100 text-amber-700';
    case 'absent':
      return 'bg-gray-100 text-gray-700';
    case 'yellow_card':
      return 'bg-yellow-100 text-yellow-700';
    case 'red_card':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export default function Fines() {
  const { user } = useAuth();
  const [fines, setFines] = useState([]);
  const [players, setPlayers] = useState([]);
  const [summary, setSummary] = useState({ total_paid: 0, total_unpaid: 0, total_fines: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFine, setEditingFine] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    player_id: '',
    category: 'late',
    description: '',
    amount: '',
    is_paid: false
  });

  useEffect(() => {
    if (user?.current_season_id) {
      fetchFines();
      fetchPlayers();
      fetchSummary();
    } else {
      setLoading(false);
    }
  }, [user?.current_season_id]);

  const fetchFines = async () => {
    try {
      const response = await axios.get(`${API}/fines`);
      setFines(response.data);
    } catch (error) {
      console.error('Failed to fetch fines:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/fines/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  // Compute fines by player
  const finesByPlayer = useMemo(() => {
    const playerMap = {};
    fines.forEach(fine => {
      if (!playerMap[fine.player_id]) {
        playerMap[fine.player_id] = {
          player_id: fine.player_id,
          player_name: fine.player_name,
          count: 0,
          total: 0
        };
      }
      playerMap[fine.player_id].count += 1;
      playerMap[fine.player_id].total += fine.amount;
    });
    return Object.values(playerMap).sort((a, b) => b.total - a.total);
  }, [fines]);

  // Compute fines by category
  const finesByCategory = useMemo(() => {
    const categoryMap = {};
    FINE_CATEGORIES.forEach(cat => {
      categoryMap[cat.value] = { category: cat.value, label: cat.label, count: 0, total: 0 };
    });
    fines.forEach(fine => {
      if (categoryMap[fine.category]) {
        categoryMap[fine.category].count += 1;
        categoryMap[fine.category].total += fine.amount;
      }
    });
    return Object.values(categoryMap).filter(c => c.count > 0).sort((a, b) => b.total - a.total);
  }, [fines]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.player_id || !formData.amount) {
      toast.error('Please select a player and enter an amount');
      return;
    }

    try {
      const payload = {
        date: formData.date,
        player_id: formData.player_id,
        category: formData.category,
        description: formData.description || null,
        amount: parseFloat(formData.amount),
        is_paid: formData.is_paid
      };

      if (editingFine) {
        await axios.put(`${API}/fines/${editingFine.id}`, payload);
        toast.success('Fine updated successfully');
      } else {
        await axios.post(`${API}/fines`, payload);
        toast.success('Fine added successfully');
      }
      fetchFines();
      fetchSummary();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fine');
    }
  };

  const handleEdit = (fine) => {
    setEditingFine(fine);
    setFormData({
      date: fine.date,
      player_id: fine.player_id,
      category: fine.category,
      description: fine.description || '',
      amount: fine.amount.toString(),
      is_paid: fine.is_paid
    });
    setDialogOpen(true);
  };

  const handleDelete = async (fineId) => {
    if (!window.confirm('Are you sure you want to delete this fine?')) return;
    try {
      await axios.delete(`${API}/fines/${fineId}`);
      toast.success('Fine deleted successfully');
      fetchFines();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to delete fine');
    }
  };

  const handleTogglePaid = async (fineId) => {
    try {
      await axios.patch(`${API}/fines/${fineId}/toggle-paid`);
      fetchFines();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      player_id: '',
      category: 'late',
      description: '',
      amount: '',
      is_paid: false
    });
    setEditingFine(null);
    setDialogOpen(false);
  };

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center page-slide-in" data-testid="no-season-message">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6 max-w-md">
            Please create or select a season to start tracking fines.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="fines-container">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
            Fines
          </h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                size="icon"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
                data-testid="add-fine-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold font-title">
                  {editingFine ? 'Edit Fine' : 'Add Fine'}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {editingFine ? 'Edit fine details' : 'Add a new fine to a player'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="player">Player</Label>
                  <Select
                    value={formData.player_id}
                    onValueChange={(value) => setFormData({ ...formData, player_id: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="fine-player-select">
                      <SelectValue placeholder="Select player" />
                    </SelectTrigger>
                    <SelectContent>
                      {players.map((player) => (
                        <SelectItem key={player.id} value={player.id}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="fine-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {FINE_CATEGORIES.map((cat) => {
                        const IconComponent = cat.icon;
                        return (
                          <SelectItem key={cat.value} value={cat.value}>
                            <span className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4" />
                              {cat.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="rounded-xl"
                    data-testid="fine-date-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., 15 minutes late to training"
                    className="rounded-xl"
                    data-testid="fine-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Fine Amount (£)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="rounded-xl"
                    data-testid="fine-amount-input"
                    required
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <Label htmlFor="is_paid" className="cursor-pointer">Paid</Label>
                  </div>
                  <Switch
                    id="is_paid"
                    checked={formData.is_paid}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_paid: checked })}
                    data-testid="fine-paid-toggle"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#064E3B] hover:bg-[#065f46] text-white rounded-xl" data-testid="save-fine-btn">
                    {editingFine ? 'Update' : 'Add'} Fine
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="stat-card card-hover" data-testid="total-paid">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-500">Paid Fines</p>
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600 mt-1">£{summary.total_paid.toFixed(2)}</p>
                </div>
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 sm:w-7 sm:h-7 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover" data-testid="total-unpaid">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-500">Unpaid Fines</p>
                  <p className="text-2xl sm:text-3xl font-bold text-red-600 mt-1">£{summary.total_unpaid.toFixed(2)}</p>
                </div>
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 sm:w-7 sm:h-7 text-red-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover" data-testid="total-fines">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-500">Total Fines</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">£{summary.total_fines.toFixed(2)}</p>
                </div>
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Gavel className="w-5 h-5 sm:w-7 sm:h-7 text-gray-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Fines List */}
        <Card data-testid="fines-list">
          <CardHeader>
            <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
              <Gavel className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
              All Fines
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading fines...</div>
            ) : fines.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Gavel className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Fines Yet</h3>
                <p className="text-gray-500 mb-4">Start tracking player fines</p>
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="btn-primary rounded-xl"
                >
                  <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Add First Fine
                </Button>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Player</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Category</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Description</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Amount</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fines.map((fine) => {
                        const CategoryIcon = getCategoryIcon(fine.category);
                        return (
                          <tr key={fine.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`fine-row-${fine.id}`}>
                            <td className="py-3 px-4 text-gray-700">{formatDateUK(fine.date)}</td>
                            <td className="py-3 px-4 text-gray-900 font-medium">{fine.player_name}</td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getCategoryColor(fine.category)}`}>
                                <CategoryIcon className="w-3 h-3" />
                                {getCategoryLabel(fine.category)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-600 text-sm">
                              {fine.description || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-gray-900">
                              £{fine.amount.toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={() => handleTogglePaid(fine.id)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                                  fine.is_paid 
                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                                data-testid={`toggle-paid-${fine.id}`}
                              >
                                {fine.is_paid ? (
                                  <>
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Paid
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-3.5 h-3.5" />
                                    Unpaid
                                  </>
                                )}
                              </button>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(fine)}
                                  className="p-2 h-auto"
                                  data-testid={`edit-fine-${fine.id}`}
                                >
                                  <Edit2 className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(fine.id)}
                                  className="p-2 h-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-fine-${fine.id}`}
                                >
                                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="sm:hidden flex flex-col gap-3">
                  {fines.map((fine) => {
                    const CategoryIcon = getCategoryIcon(fine.category);
                    return (
                      <div 
                        key={fine.id} 
                        className="p-4 bg-gray-50 rounded-xl"
                        data-testid={`fine-card-${fine.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getCategoryColor(fine.category)}`}>
                              <CategoryIcon className="w-4 h-4" strokeWidth={1.5} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{fine.player_name}</p>
                              <p className="text-xs text-gray-500">{getCategoryLabel(fine.category)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(fine)}
                              className="p-1.5 h-auto"
                            >
                              <Edit2 className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(fine.id)}
                              className="p-1.5 h-auto text-red-500"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </div>
                        {fine.description && (
                          <p className="text-sm text-gray-600 mb-3 pl-10">{fine.description}</p>
                        )}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                          <span className="text-xs text-gray-500">{formatDateUK(fine.date)}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-gray-900">£{fine.amount.toFixed(2)}</span>
                            <button
                              onClick={() => handleTogglePaid(fine.id)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                fine.is_paid 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {fine.is_paid ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" />
                                  Paid
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-3 h-3" />
                                  Unpaid
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Summary Sections */}
        {fines.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Fines by Player */}
            <Card data-testid="fines-by-player">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-title flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                  Fines by Player
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {finesByPlayer.map((player, index) => {
                    const maxTotal = finesByPlayer[0]?.total || 1;
                    const percentage = (player.total / maxTotal) * 100;
                    return (
                      <div key={player.player_id} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-900">{player.player_name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{player.count} fine{player.count !== 1 ? 's' : ''}</span>
                            <span className="font-semibold text-gray-900">£{player.total.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-400 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {finesByPlayer.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No fines recorded yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Fines by Category */}
            <Card data-testid="fines-by-category">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-title flex items-center gap-2">
                  <Gavel className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                  Fines by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {finesByCategory.map((cat) => {
                    const CategoryIcon = getCategoryIcon(cat.category);
                    const maxTotal = finesByCategory[0]?.total || 1;
                    const percentage = (cat.total / maxTotal) * 100;
                    return (
                      <div key={cat.category} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`p-1 rounded ${getCategoryColor(cat.category)}`}>
                              <CategoryIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </span>
                            <span className="font-medium text-gray-900">{cat.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{cat.count} fine{cat.count !== 1 ? 's' : ''}</span>
                            <span className="font-semibold text-gray-900">£{cat.total.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-300 ${
                              cat.category === 'late' ? 'bg-amber-400' :
                              cat.category === 'absent' ? 'bg-gray-400' :
                              cat.category === 'yellow_card' ? 'bg-yellow-400' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {finesByCategory.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No fines recorded yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
